/**
 * SaveThemeDialog - Dialog for saving customized theme and applying to project
 */

import { Loader2, Shuffle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useResponsive } from "@/hooks/useResponsive";
import { cn } from "@/lib/utils";

import { ColorSwatch } from "./color-swatch";

interface SaveThemeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  themeName: string;
  onThemeNameChange: (name: string) => void;
  previewColors: string[];
  onColorChange: (index: number, color: string) => void;
  onShuffleColors: () => void;
  onSave: () => void;
  isSaving: boolean;
  isApplying: boolean;
}

function SaveThemeDialog({
  open,
  onOpenChange,
  themeName,
  onThemeNameChange,
  previewColors,
  onColorChange,
  onShuffleColors,
  onSave,
  isSaving,
  isApplying,
}: SaveThemeDialogProps) {
  const { isMobile } = useResponsive();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(isMobile && "w-[calc(100%-2rem)] max-w-[calc(100%-2rem)]")}>
        <DialogHeader>
          <DialogTitle>Save & Apply Theme</DialogTitle>
          <DialogDescription>
            Save your customized theme and apply it to the project.
          </DialogDescription>
        </DialogHeader>

        {/* Theme Name Input */}
        <div className="mt-4">
          <Label htmlFor="save-theme-name" className="mb-1.5 block text-xs">
            Theme Name
          </Label>
          <Input
            id="save-theme-name"
            value={themeName}
            onChange={(e) => onThemeNameChange(e.target.value)}
            placeholder="Enter theme name"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSave();
              }
            }}
          />
        </div>

        {/* Preview Colors */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs">Preview Colors</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onShuffleColors}
              className="h-7 gap-1 text-xs"
            >
              <Shuffle className="h-3 w-3" />
              Shuffle
            </Button>
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {previewColors.map((color, index) => (
              <ColorSwatch
                key={index}
                color={color}
                index={index}
                onChange={onColorChange}
              />
            ))}
          </div>
        </div>

        <DialogFooter className={cn("mt-6", isMobile && "flex-col gap-2")}>
          {isMobile ? (
            <>
              <Button
                onClick={onSave}
                disabled={isSaving || isApplying || !themeName.trim()}
                className="w-full"
              >
                {isSaving || isApplying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isSaving ? "Saving..." : "Applying..."}
                  </>
                ) : (
                  "Save & Apply"
                )}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={onSave}
                disabled={isSaving || isApplying || !themeName.trim()}
              >
                {isSaving || isApplying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isSaving ? "Saving..." : "Applying..."}
                  </>
                ) : (
                  "Save & Apply"
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { SaveThemeDialog };
